import { Box, Flex, Field, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";

import { FormNumberInput } from "./FormNumberInput";
import { useFormContext } from "react-hook-form";
import { InvoiceFormData } from "@/types";
import { useCallback } from "react";
import { Radio, RadioGroup, Heading } from "@digital-services-gard-as/anchor-react";

export const AdvanceOfFunds = () => {
    const { t } = useTranslation(["invoices", "main"]);
    const { watch, setValue } = useFormContext<InvoiceFormData>();

    const hasAdvanceOfFunds = watch("hasAdvanceOfFunds");

    const handleAdvanceOfFundRadioChange = useCallback(
        (value: string) => {
            setValue("hasAdvanceOfFunds", value === "true");
        },
        [setValue]
    );

    return (
        <Box bg="white" p={{ base: "1rem", md: "1.5rem" }} borderRadius="md" boxShadow="xs">
            <Heading size="md">{t("createInvoice.feesAndExpenses.advanceOfFund")}</Heading>
            <Field.Root mb={4}>
                <RadioGroup
                    legend={t("invoices:createInvoice.feesAndExpenses.advanceOfFundsQuestion")}
                    value={hasAdvanceOfFunds?.toString()}
                    onChange={handleAdvanceOfFundRadioChange}
                    data-testid="hasAdvanceOfFunds"
                >
                    <Radio value="true" size="sm">
                        <Text fontSize="md">{t("main:yes")}</Text>
                    </Radio>
                    <Radio value="false" size="sm">
                        <Text fontSize="md">{t("main:no")}</Text>
                    </Radio>
                </RadioGroup>
            </Field.Root>
            <ShowAdvanceOfFundsInputs hasAdvanceOfFunds={hasAdvanceOfFunds} />
        </Box>
    );
};

type ShowAdvanceOfFundsInputsProps = {
    hasAdvanceOfFunds: boolean | undefined;
};

const ShowAdvanceOfFundsInputs = ({ hasAdvanceOfFunds }: ShowAdvanceOfFundsInputsProps) => {
    const { t } = useTranslation("invoices");
    const {
        watch,
        control,
        formState: { errors }
    } = useFormContext<InvoiceFormData>();
    const currency = watch("bankAccount.currency");

    if (!hasAdvanceOfFunds) {
        return null;
    }

    return (
        <Flex flexDir="column" gap="2.5rem">
            <FormNumberInput
                label={t("invoices:createInvoice.feesAndExpenses.advanceOfFunds")}
                id="advanceOfFunds"
                errorMessage={errors.advanceOfFunds?.message}
                isRequired={false}
                control={control}
                currency={currency}
                allowNegative={false}
            />

            <FormNumberInput
                label={t("invoices:createInvoice.feesAndExpenses.previousAdvanceOfFunds")}
                id="previousAdvanceOfFunds"
                errorMessage={errors.previousAdvanceOfFunds?.message}
                isRequired={false}
                control={control}
                currency={currency}
                allowNegative={false}
            />
        </Flex>
    );
};
